import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import NamedBox from '../atoms/NamedBox';
import { COLOR_BACKGROUND_WARM, COLOR_BORDER, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { JUCOProductionRow } from '../types/CollegeProductionData';


interface JucoProductionTableProps {
  className?: string;
  data?: JUCOProductionRow[] | null;
}

const useStyles = makeStyles((theme: Theme) => {
  const firstColWidth = 70;
  const secondColWidth = 170;

  return {
    root: {},
    headerName: {
      fontFamily: FONT_PROXIMA_NOVA,
    },
    tableContainer: {
      maxHeight: 360,
      overflow: 'auto',
    },
    table: {
      width: '100%',
      minWidth: 960,
      borderCollapse: 'collapse' as const,
      tableLayout: 'fixed' as const,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(12),
    },
    theadCell: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      // background: COLOR_BACKGROUND_WARM,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      // zIndex: 2,
    },
    theadGroupCell: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      background: COLOR_MIDDLE_GRAY,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      // zIndex: 2,
    },
    stickyHeaderTop: {
      position: 'sticky' as const,
      top: 0,
    },
    stickyHeaderSecond: {
      position: 'sticky' as const,
      top: 32,
    },
    thFirstCol: {
      minWidth: firstColWidth,
      maxWidth: firstColWidth,
      left: 0,
      // zIndex: 3,
    },
    thSecondCol: {
      minWidth: secondColWidth,
      maxWidth: secondColWidth,
      left: firstColWidth,
      // zIndex: 3,
    },
    stickyCol: {
      position: 'sticky' as const,
      // background: COLOR_BACKGROUND_WARM,
    },
    bodyRow: {
      background: COLOR_WHITE,
    },
    bodyCell: {
      borderBottom: `1px solid ${COLOR_MIDDLE_GRAY}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(0.75, 1),
      textAlign: 'center' as const,
      whiteSpace: 'nowrap' as const,
    },
    bodyCellFirst: {
      position: 'sticky' as const,
      left: 0,
      minWidth: firstColWidth,
      maxWidth: firstColWidth,
      background: COLOR_WHITE,
      // zIndex: 2,
      textAlign: 'left' as const,
    },
    bodyCellSecond: {
      position: 'sticky' as const,
      left: firstColWidth,
      minWidth: secondColWidth,
      maxWidth: secondColWidth,
      background: COLOR_WHITE,
      // zIndex: 2,
      textAlign: 'left' as const,
    },
    yearCell: {
      fontWeight: 700,
    },
    groupHeaderRow: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
    },
    groupHeaderCell: {
      background: COLOR_BACKGROUND_WARM,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      fontWeight: 700,
      textAlign: 'center' as const,
    },
  };
}, { name: 'JucoProductionTable' });

export default function JucoProductionTable(props: JucoProductionTableProps) {
  const { className, data } = props;
  const classes = useStyles();

  // const MOCK_JUCO_PRODUCTION: JucoProductionResponse = {
  //   idType: 'tfPlayerId',
  //   idValue: 10000,
  //   productionData: [
  //     {
  //       year: 2025,
  //       teamIdType: 'tfId',
  //       teamIdValue: 866,
  //       teamName: 'Diablo Valley',
  //       gamesPlayed: 7,
  //       gamesStarted: 7,
  //       passingComp: 0,
  //       passingAtt: 0,
  //       passingCompletionPercent: 0,
  //       passingYds: 0,
  //       passingYPA: 0,
  //       passingTD: 0,
  //       passingInt: 0,
  //       rushingAtt: 0,
  //       rushingYds: 0,
  //       rushingYPC: 0,
  //       rushingTD: 0,
  //       receiving: 19,
  //       receivingYds: 310,
  //       receivingYPC: 16.3,
  //       receivingTD: 3,
  //       defenseSoloTackles: 1,
  //       defenseAssistedTackles: 0,
  //       defenseTotalTackles: 1,
  //       defenseSack: 0,
  //       defenseSackYds: 0,
  //       defenseTFL: 0,
  //       defenseTFLYds: 0,
  //       defensePassBreak: 0,
  //       defenseInt: 0,
  //       defenseFF: 0,
  //       kickingFGM: 0,
  //       kickingFGA: 0,
  //       kickingXP: 0,
  //       kickingXPA: 0,
  //       punts: 0,
  //       puntYds: 0,
  //       puntsAverage: 0,
  //       kickReturns: 7,
  //       kickReturnYds: 143,
  //       kickReturnAverage: 20,
  //       kickReturnTD: 0,
  //       puntRet: 1,
  //       puntRetYds: 5,
  //       puntRetAverage: 5,
  //       puntRetTD: 0,
  //     },
  //   ],
  // };

  // const rows: JUCOProductionRow[] = (data ?? MOCK_JUCO_PRODUCTION).productionData;
  const rows: JUCOProductionRow[] = data || [];

  return (
    <div>
      <NamedBox
        className={clsx(classes.root, className)}
        headerNameClassName={classes.headerName}
        name="JUCO Offensive Stats"
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th
                  className={clsx(
                    classes.theadCell,
                    classes.stickyHeaderTop,
                    classes.stickyCol,
                    classes.thFirstCol,
                  )}
                  rowSpan={2}
                >
                  Year
                </th>
                <th
                  className={clsx(
                    classes.theadCell,
                    classes.stickyHeaderTop,
                    classes.stickyCol,
                    classes.thSecondCol,
                  )}
                  rowSpan={2}
                >
                  Team
                </th>
                <th
                  className={clsx(classes.theadCell, classes.stickyHeaderTop)}
                  rowSpan={2}
                >
                  G
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={5}
                >
                  Passing
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  Rushing
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  Receiving
                </th>
                {/* <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  Defense
                </th> */}
              </tr>
              <tr className={classes.groupHeaderRow}>
                {/* Passing */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Comp</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Att</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>INT</th>
                {/* Rushing */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Att</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
                {/* Receiving */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Rec</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
                {/* Defense */}
                {/* <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Tckls</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Sacks</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>INTs</th> */}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.year}-${row.teamName}`} className={classes.bodyRow}>
                  <td className={clsx(classes.bodyCell, classes.bodyCellFirst, classes.yearCell)}>
                    {row.year}
                  </td>
                  <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                    {row.teamName}
                  </td>
                  <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                  {/* Passing */}
                  <td className={classes.bodyCell}>{row.passingComp}</td>
                  <td className={classes.bodyCell}>{row.passingAtt}</td>
                  <td className={classes.bodyCell}>{row.passingYds}</td>
                  <td className={classes.bodyCell}>{row.passingTD}</td>
                  <td className={classes.bodyCell}>{row.passingInt}</td>
                  {/* Rushing */}
                  <td className={classes.bodyCell}>{row.rushingAtt}</td>
                  <td className={classes.bodyCell}>{row.rushingYds}</td>
                  <td className={classes.bodyCell}>{row.rushingTD}</td>
                  {/* Receiving */}
                  <td className={classes.bodyCell}>{row.receiving}</td>
                  <td className={classes.bodyCell}>{row.receivingYds}</td>
                  <td className={classes.bodyCell}>{row.receivingTD}</td>
                  {/* Defense */}
                  {/* <td className={classes.bodyCell}>{row.defense.tckls}</td>
                  <td className={classes.bodyCell}>{row.defense.sacks}</td>
                  <td className={classes.bodyCell}>{row.defense.ints}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NamedBox>
      <NamedBox
      className={clsx(classes.root)}
      headerNameClassName={classes.headerName}
      name="JUCO Defensive Stats"
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th
                  className={clsx(
                    classes.theadCell,
                    classes.stickyHeaderTop,
                    classes.stickyCol,
                    classes.thFirstCol,
                  )}
                  rowSpan={2}
                >
                  Year
                </th>
                <th
                  className={clsx(
                    classes.theadCell,
                    classes.stickyHeaderTop,
                    classes.stickyCol,
                    classes.thSecondCol,
                  )}
                  rowSpan={2}
                >
                  Team
                </th>
                <th
                  className={clsx(classes.theadCell, classes.stickyHeaderTop)}
                  rowSpan={2}
                >
                  G
                </th>
                {/* <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={5}
                >
                  Passing
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  Rushing
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  Receiving
                </th> */}
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  {/* Receiving */}
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  Defense
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  {/* Receiving */}
                </th>
              </tr>
              <tr className={classes.groupHeaderRow}>
                {/* Passing */}
                {/* <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Comp</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Att</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>INT</th> */}
                {/* Rushing */}
                {/* <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Att</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th> */}
                {/* Receiving */}
                {/* <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Rec</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th> */}
                {/* Defense */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Solo Tckls</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Asst Tckls</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Total Tckls</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Sacks</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Sack Yards</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TFLs</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TFL Yards</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>PBUs</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>INTs</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>FFs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.year}-${row.teamName}-def`} className={classes.bodyRow}>
                  <td className={clsx(classes.bodyCell, classes.bodyCellFirst, classes.yearCell)}>
                    {row.year}
                  </td>
                  <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                    {row.teamName}
                  </td>
                  <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                  {/* Passing */}
                  {/* <td className={classes.bodyCell}>{row.passing.comp}</td>
                  <td className={classes.bodyCell}>{row.passing.att}</td>
                  <td className={classes.bodyCell}>{row.passing.yds}</td>
                  <td className={classes.bodyCell}>{row.passing.td}</td>
                  <td className={classes.bodyCell}>{row.passing.int}</td> */}
                  {/* Rushing */}
                  {/* <td className={classes.bodyCell}>{row.rushing.att}</td>
                  <td className={classes.bodyCell}>{row.rushing.yds}</td>
                  <td className={classes.bodyCell}>{row.rushing.td}</td> */}
                  {/* Receiving */}
                  {/* <td className={classes.bodyCell}>{row.receiving.rec}</td>
                  <td className={classes.bodyCell}>{row.receiving.yds}</td>
                  <td className={classes.bodyCell}>{row.receiving.td}</td> */}
                  {/* Defense */}
                  <td className={classes.bodyCell}>{row.defenseSoloTackles}</td>
                  <td className={classes.bodyCell}>{row.defenseAssistedTackles}</td>
                  <td className={classes.bodyCell}>{row.defenseTotalTackles}</td>
                  <td className={classes.bodyCell}>{row.defenseSack}</td>
                  <td className={classes.bodyCell}>{row.defenseSackYds}</td>
                  <td className={classes.bodyCell}>{row.defenseTFL}</td>
                  <td className={classes.bodyCell}>{row.defenseTFLYds}</td>
                  <td className={classes.bodyCell}>{row.defensePassBreak}</td>
                  <td className={classes.bodyCell}>{row.defenseInt}</td>
                  <td className={classes.bodyCell}>{row.defenseFF}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NamedBox>
      <NamedBox
      className={clsx(classes.root)}
      headerNameClassName={classes.headerName}
      name="JUCO Special Teams Stats"
      >
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th
                  className={clsx(
                    classes.theadCell,
                    classes.stickyHeaderTop,
                    classes.stickyCol,
                    classes.thFirstCol,
                  )}
                  rowSpan={2}
                >
                  Year
                </th>
                <th
                  className={clsx(
                    classes.theadCell,
                    classes.stickyHeaderTop,
                    classes.stickyCol,
                    classes.thSecondCol,
                  )}
                  rowSpan={3}
                >
                  Team
                </th>
                <th
                  className={clsx(classes.theadCell, classes.stickyHeaderTop)}
                  rowSpan={2}
                >
                  G
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  Kicking
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={3}
                >
                  Punting
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  Kick Returns
                </th>
                <th
                  className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                  colSpan={4}
                >
                  Punt Returns
                </th>
              </tr>
              <tr className={classes.groupHeaderRow}>
                {/* Kicking */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>FGM</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>FGA</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>XP</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>XPA</th>
                {/* Punting */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Punts</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Avg</th>
                {/* Kick Returns */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>KR</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Avg</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
                {/* Punt Returns */}
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>PR</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Avg</th>
                <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.year}-${row.teamName}-st`} className={classes.bodyRow}>
                  <td className={clsx(classes.bodyCell, classes.bodyCellFirst, classes.yearCell)}>
                    {row.year}
                  </td>
                  <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                    {row.teamName}
                  </td>
                  <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                  {/* Kicking */}
                  <td className={classes.bodyCell}>{row.kickingFGM}</td>
                  <td className={classes.bodyCell}>{row.kickingFGA}</td>
                  <td className={classes.bodyCell}>{row.kickingXP}</td>
                  <td className={classes.bodyCell}>{row.kickingXPA}</td>
                  {/* Punting */}
                  <td className={classes.bodyCell}>{row.punts}</td>
                  <td className={classes.bodyCell}>{row.puntYds}</td>
                  <td className={classes.bodyCell}>{row.puntsAverage}</td>
                  {/* Kick Returns */}
                  <td className={classes.bodyCell}>{row.kickReturns}</td>
                  <td className={classes.bodyCell}>{row.kickReturnYds}</td>
                  <td className={classes.bodyCell}>{row.kickReturnAverage}</td>
                  <td className={classes.bodyCell}>{row.kickReturnTD}</td>
                  {/* Punt Returns */}
                  <td className={classes.bodyCell}>{row.puntRet}</td>
                  <td className={classes.bodyCell}>{row.puntRetYds}</td>
                  <td className={classes.bodyCell}>{row.puntRetAverage}</td>
                  <td className={classes.bodyCell}>{row.puntRetTD}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </NamedBox>
    </div>
  );
}

